// Interchange — Wirex partner share blended with the per-month regional gross
// interchange rate. Current deal sheet excludes interchange sharing in year 1;
// years 2 and 3 use the 10/30/50% GMV tier ladder.
//
// Engine math:
//   Pulsar's effective interchange capture = blendedRegionalInterchange × wirexShare
//   wirexShare = 0% in year 1, then volume-weighted across the tier ladder
//   for monthly GMV straddling the cumulative-GMV breakpoints.

import { blendedRegionalInterchange } from './regions.js';

/**
 * Volume-weighted Wirex partner share for a single month straddling tier
 * breakpoints. Returns share as a decimal in [tier1Share, tier3Share].
 */
export function wirexShareForMonth(cumStart, monthlyVol, wirex, month = 1) {
  if (month <= 12) return wirex.interchangeShareY1 ?? 0;
  if (monthlyVol <= 0) return wirex.tier1Share;

  let cum = cumStart;
  let remaining = monthlyVol;
  let weighted = 0;

  // Tier 1
  const t1space = Math.max(0, wirex.tier1Cap - cum);
  const t1use = Math.min(remaining, t1space);
  weighted += t1use * wirex.tier1Share;
  remaining -= t1use;
  cum += t1use;

  // Tier 2
  const t2space = Math.max(0, wirex.tier2Cap - cum);
  const t2use = Math.min(remaining, t2space);
  weighted += t2use * wirex.tier2Share;
  remaining -= t2use;
  cum += t2use;

  // Tier 3 — anything past tier2Cap
  weighted += remaining * wirex.tier3Share;

  return weighted / monthlyVol;
}

/**
 * Effective Pulsar interchange capture for the month. This is the rate the
 * forecast multiplies by total GMV to get Pulsar's interchange revenue.
 *
 * Returns:
 *   {
 *     grossRate,   // blended regional gross interchange (decimal)
 *     wirexShare,  // Pulsar's partner share for this month
 *     pulsarRate,  // grossRate × wirexShare — Pulsar's effective capture
 *   }
 */
export function effectiveInterchange(inputs, month, cumStart, monthlyVol) {
  const grossRate = blendedRegionalInterchange(inputs, month);
  const wirexShare = wirexShareForMonth(cumStart, monthlyVol, inputs.wirex, month);
  return { grossRate, wirexShare, pulsarRate: grossRate * wirexShare };
}
